/** @jsx jsx */
import { jsx } from '@emotion/core';
import { withSitecoreContext, Text } from '@sitecore-jss/sitecore-jss-react';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import Swiper from 'react-id-swiper';
import defaultsDeep from 'lodash/defaultsDeep';
import compose from 'lodash/fp/compose';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import Card from '../../ui/Card';
import CtaLink from '../../ui/CtaLink';
import { arrows } from '../../ui/CtaLink/types';
import DotComponent from '../../ui/DotComponent';
import Spacer from '../../ui/Spacer';
import { componentHasStickyLabel } from '../StickyBar';
import { Container, Row, Column } from '../../../styles/Grid';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import {
  transformWildcardsToText,
  getWildcardData,
} from '../../../utils/wildcards';
import { swiperParams } from './swiperConfigs';
import * as styles from './index.styles';

const defaultData = {
  Headline: {
    value: 'Featured {GeoSolutionName} locations',
  },
  Locations: {
    value: [],
  },
  IsGeoSolution: false,
  SeeListingsCTA: {
    value: {
      anchor: '',
      href: '',
      id: '',
      linktype: 'internal',
      querystring: '',
      target: '',
    },
  },
};

export class PopularInThisArea extends SafePureComponent {
  constructor(props) {
    super(props);
    this.fields = defaultsDeep(this.props.fields, defaultData);
    this.wildcards = getWildcardData(this.props.sitecoreContext);
  }

  /**
   * This function is rendering the Headline of the component and transforming the wildcards values to be used inside the Headline
   * @param  {boolean} shouldRenderSeeListingsCTA validation with the information about if thr view Location Cta should be Rendered
   * @return {React.Node} Node with the Headline of the component
   */
  renderHeadline = shouldRenderSeeListingsCTA => {
    const { t } = this.props;
    const { IsGeoSolution } = this.fields;
    const HeadLineTitle = get(this.fields, 'Headline.value');
    if (IsGeoSolution) {
      const contextFields = get(this.props, 'sitecoreContext.route.fields', {});
      const componentName = get(this.props, 'rendering.componentName');
      const headlineCopy = t('PopularInThisAreaHeadlineWildcard');
      const Headline = {
        value: transformWildcardsToText({
          componentName,
          contextFields,
          fields: this.fields,
          text: headlineCopy,
          wildcards: this.wildcards,
          t,
        }),
      };
      return (
        <h2 css={styles.headline(shouldRenderSeeListingsCTA)}>
          <Text field={Headline} />
          <DotComponent />
        </h2>
      );
    }
    return (
      <h2 css={styles.headline(shouldRenderSeeListingsCTA)}>
        {HeadLineTitle}
        <DotComponent />
      </h2>
    );
  };

  /**
   * This function is rendering the See Locations Cta of the component and transforming the wildcards values to be used inside the Headline
   * @return {React.Node} Node with the See Location of the component
   */
  renderSeeLocationsCTA = () => {
    const { t } = this.props;
    const contextFields = get(this.props, 'sitecoreContext.route.fields', {});
    const componentName = get(this.props, 'rendering.componentName');
    const text = t('SeeListingsCTA');
    const SeeListingsCTA = get(this.fields, 'SeeListingsCTA', {});
    const Text = transformWildcardsToText({
      componentName,
      contextFields,
      fields: this.fields,
      text,
      wildcards: this.wildcards,
      t,
    });
    const ctaField = {
      value: {
        href: SeeListingsCTA,
        text: Text,
      },
    };
    return (
      <CtaLink
        arrow={arrows.right}
        cta={ctaField}
        componentName={componentName}
      />
    );
  };

  safeRender() {
    const { props } = this;
    const Locations = get(this.fields, 'Locations.value', []);
    const SeeListingsCTA = get(this.fields, 'SeeListingsCTA', {});
    const hasStickyLabel = componentHasStickyLabel(props);
    const { IsGeoSolution } = this.fields;
    const shouldRenderSeeListingsCTA = !!SeeListingsCTA && IsGeoSolution;

    if (isEmpty(Locations)) return null;

    return (
      <Spacer
        marginCss={styles.containerMargin}
        containerProps={{
          'data-sticky-id': hasStickyLabel && get(props, 'rendering.uid'),
        }}
      >
        <Container>
          <Row>
            <Column cols={{ sm: 6, md: 8, lg: 9 }}>
              {this.renderHeadline(shouldRenderSeeListingsCTA)}
            </Column>
            <Column css={styles.ctaWrapper} cols={{ sm: 6, md: 8, lg: 3 }}>
              {shouldRenderSeeListingsCTA && this.renderSeeLocationsCTA()}
            </Column>
            <Column cols={{ sm: 6, md: 8, lg: 12 }}>
              <div css={styles.divider} />
            </Column>
          </Row>
          <div css={[styles.swiperContainer]}>
            <Swiper data-testid="popular-in-swiper" {...swiperParams}>
              {Locations.length > 0 &&
                Locations.map((card, i) => (
                  <div data-c="jus-test" key={`location-card-${i}`}>
                    <Card fields={card} />
                  </div>
                ))}
            </Swiper>
          </div>
        </Container>
      </Spacer>
    );
  }
}

const objectValueType = {
  value: PropTypes.string,
};

const ctaType = {
  value: {
    href: PropTypes.string,
    label: PropTypes.string,
    linktype: PropTypes.string,
    url: PropTypes.string,
  },
};

const locationPrice = {
  SolutionName: PropTypes.shape(objectValueType),
  SolutionPrice: PropTypes.shape({
    Currency: PropTypes.string,
    Price: PropTypes.number,
    PriceUnit: PropTypes.string,
    ShowCurrencySymbolAfter: PropTypes.bool,
  }),
};

const locationType = {
  LocationAddress: PropTypes.shape(objectValueType),
  LocationHeroImage: PropTypes.shape(objectValueType),
  ParentLocationName: PropTypes.any,
  Prices: PropTypes.arrayOf(PropTypes.shape(locationPrice)),
  ProtonName: PropTypes.shape(objectValueType),
  SeeLocationsCTA: PropTypes.any,
  Url: PropTypes.shape(objectValueType),
};

PopularInThisArea.displayName = 'PopularInThisArea';

PopularInThisArea.defaultProps = { fields: defaultData, t: key => key };

PopularInThisArea.propTypes = {
  GetInTouch: PropTypes.shape(ctaType),
  IsGeoSolution: PropTypes.bool,
  Locations: PropTypes.shape({
    value: PropTypes.arrayOf(PropTypes.shape(locationType)),
  }),
  MoreIn: PropTypes.shape(ctaType),
  t: PropTypes.func,
};

export default compose(
  withSitecoreContext(),
  withNamespaces(),
)(PopularInThisArea);
